import { http } from '../index';
import { jobTasksModifier } from '../formatters';

const withCompanyId = (companyId, params = {}) => ({
  ...params,
  companyId,
});

export const getProvisionedTrainingModulesList = (companyId, params = {}) =>
  http
    .get('/hyper-admin/provisioned/training-modules/list', {
      params: withCompanyId(companyId, params),
    })
    .then(res => res.data)
    .catch(error => error.response?.data || error);

export const getProvisionedTrainingModule = (companyId, id) =>
  http
    .get(`/hyper-admin/provisioned/training-modules/${id}`, {
      params: withCompanyId(companyId),
    })
    .then(res => res.data)
    .catch(error => error.response?.data || error);

export const updateProvisionedTrainingModule = (companyId, id, formData) =>
  http
    .put(`/hyper-admin/provisioned/training-modules/${id}`, formData, {
      params: withCompanyId(companyId),
    })
    .then(res => res.data)
    .catch(error => error.response?.data || error);

export const getProvisionedTrainingPartsList = (companyId, params = {}) =>
  http
    .get('/hyper-admin/provisioned/training-parts/list', {
      params: withCompanyId(companyId, params),
    })
    .then(res => res.data)
    .catch(error => error.response?.data || error);

export const getProvisionedTrainingPart = (companyId, id) =>
  http
    .get(`/hyper-admin/provisioned/training-parts/${id}`, {
      params: withCompanyId(companyId),
    })
    .then(res => res.data)
    .catch(error => error.response?.data || error);

export const updateProvisionedTrainingPart = (companyId, id, formData) =>
  http
    .put(`/hyper-admin/provisioned/training-parts/${id}`, formData, {
      params: withCompanyId(companyId),
    })
    .then(res => res.data)
    .catch(error => error.response?.data || error);

export const getProvisionedTrainingPartLmsLanguageStatus = (companyId, trainingPartId) =>
  http
    .get(`/hyper-admin/provisioned/training-parts/${trainingPartId}/lms-language-status`, {
      params: withCompanyId(companyId),
    })
    .then(res => res.data)
    .catch(error => error.response?.data || error);

export const syncProvisionedTrainingPartLmsLanguages = (companyId, trainingPartId) =>
  http
    .post(
      `/hyper-admin/provisioned/training-parts/${trainingPartId}/sync-lms-languages`,
      {},
      {
        params: withCompanyId(companyId),
      },
    )
    .then(res => res.data)
    .catch(error => error.response?.data || error);

export const getProvisionedJobRolesList = (companyId, params = {}) =>
  http
    .get('/hyper-admin/provisioned/job-roles/list', {
      params: withCompanyId(companyId, params),
    })
    .then(res => jobTasksModifier(res.data))
    .catch(error => error.response?.data || error);

export const getProvisionedJobRole = (companyId, id) =>
  http
    .get(`/hyper-admin/provisioned/job-roles/${id}`, {
      params: withCompanyId(companyId),
    })
    .then(res => res.data)
    .catch(error => error.response?.data || error);

export const updateProvisionedJobRole = (companyId, id, formData) =>
  http
    .put(`/hyper-admin/provisioned/job-roles/${id}`, formData, {
      params: withCompanyId(companyId),
    })
    .then(res => res.data)
    .catch(error => error.response?.data || error);

const LAUNCHED_TRAINING_PART_STATUSES = new Set(['scheduled', 'active', 'retired']);

export const createProvisionedTrainingPart = (companyId, formData) =>
  http
    .post('/hyper-admin/provisioned/training-parts', formData, {
      params: withCompanyId(companyId),
    })
    .then(res => res.data)
    .catch(error => error.response?.data || error);

export const launchProvisionedTrainingPart = (companyId, id) =>
  http
    .post(`/hyper-admin/provisioned/training-parts/${id}/launch`, {}, {
      params: withCompanyId(companyId),
    })
    .then(res => res.data)
    .catch(error => error.response?.data || error);

export const cancelProvisionedTrainingPart = (companyId, id) =>
  http
    .post(`/hyper-admin/provisioned/training-parts/${id}/cancel`, {}, {
      params: withCompanyId(companyId),
    })
    .then(res => res.data)
    .catch(error => error.response?.data || error);

export const deleteProvisionedTrainingPart = (companyId, id) =>
  http
    .delete(`/hyper-admin/provisioned/training-parts/${id}`, {
      params: withCompanyId(companyId),
    })
    .then(res => res.data)
    .catch(error => error.response?.data || error);

export const getPreviousProvisionedTrainingPart = async (companyId, trainingModuleId, trainingMethod) => {
  const result = await getProvisionedTrainingPartsList(companyId, {
    trainingModuleId,
    trainingMethod,
    page: 0,
    pageSize: 100,
    complianceFrom: 'DESC',
  });

  if (result?.error || result?.statusCode) {
    return result;
  }

  const previous =
    (result?.items || []).find(item => LAUNCHED_TRAINING_PART_STATUSES.has(item.status)) || null;

  return {
    ...result,
    items: previous ? [previous] : [],
    count: previous ? 1 : 0,
  };
};

export const getProvisionedJobRoleVersions = (companyId, jobRoleId, params = {}) =>
  http
    .get(`/hyper-admin/provisioned/job-roles/${jobRoleId}/versions/list`, {
      params: withCompanyId(companyId, params),
    })
    .then(res => res.data)
    .catch(error => error.response?.data || error);

export const getProvisionedJobRoleVersion = (companyId, jobRoleId, versionId) =>
  http
    .get(`/hyper-admin/provisioned/job-roles/${jobRoleId}/versions/${versionId}`, {
      params: withCompanyId(companyId),
    })
    .then(res => res.data)
    .catch(error => error.response?.data || error);

export const createProvisionedJobRoleVersion = (companyId, jobRoleId, formData) =>
  http
    .post(`/hyper-admin/provisioned/job-roles/${jobRoleId}/versions`, formData, {
      params: withCompanyId(companyId),
    })
    .then(res => res.data)
    .catch(error => error.response?.data || error);

export const updateProvisionedJobRoleVersion = (companyId, jobRoleId, versionId, formData) =>
  http
    .put(`/hyper-admin/provisioned/job-roles/${jobRoleId}/versions/${versionId}`, formData, {
      params: withCompanyId(companyId),
    })
    .then(res => res.data)
    .catch(error => error.response?.data || error);

export const launchProvisionedJobRoleVersion = (companyId, jobRoleId, versionId) =>
  http
    .post(`/hyper-admin/provisioned/job-roles/${jobRoleId}/versions/${versionId}/launch`, {}, {
      params: withCompanyId(companyId),
    })
    .then(res => res.data)
    .catch(error => error.response?.data || error);

export const cancelProvisionedJobRoleVersion = (companyId, jobRoleId, versionId) =>
  http
    .post(`/hyper-admin/provisioned/job-roles/${jobRoleId}/versions/${versionId}/cancel`, {}, {
      params: withCompanyId(companyId),
    })
    .then(res => res.data)
    .catch(error => error.response?.data || error);

export const deleteProvisionedJobRoleVersion = (companyId, jobRoleId, versionId) =>
  http
    .delete(`/hyper-admin/provisioned/job-roles/${jobRoleId}/versions/${versionId}`, {
      params: withCompanyId(companyId),
    })
    .then(res => res.data)
    .catch(error => error.response?.data || error);

export const getProvisionedJobRoleVersionRequirements = (companyId, jobRoleId, versionId) =>
  http
    .get(`/hyper-admin/provisioned/job-roles/${jobRoleId}/versions/${versionId}/requirements`, {
      params: withCompanyId(companyId),
    })
    .then(res => res.data)
    .catch(error => error.response?.data || error);

export const replaceProvisionedJobRoleVersionRequirements = (companyId, jobRoleId, versionId, data) =>
  http
    .put(`/hyper-admin/provisioned/job-roles/${jobRoleId}/versions/${versionId}/requirements`, data, {
      params: withCompanyId(companyId),
    })
    .then(res => res.data)
    .catch(error => error.response?.data || error);

export const getProvisionedJobRoleVersionRequirementsDiff = (
  companyId,
  jobRoleId,
  versionId,
  compareVersionId,
) =>
  http
    .get(`/hyper-admin/provisioned/job-roles/${jobRoleId}/versions/${versionId}/requirements/diff`, {
      params: withCompanyId(companyId, { compareVersionId }),
    })
    .then(res => res.data)
    .catch(error => error.response?.data || error);
